import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { API_BASE } from '../../config/api';
import ExternalCourseThumbnail, { thumbnailErrorMessage } from './ExternalCourseThumbnail';

const EMPTY = { title: '', iframe_url: '', description: '', specialisation: '', original_category: '', level: 'Introductory', estimated_duration: '', cpd_points: 0, provider: 'Old Mutual', status: 'draft' };
const FIELDS = [
  ['title', 'Title *'], ['iframe_url', 'Course link (https://) *'], ['specialisation', 'Specialisation'],
  ['original_category', 'Sub-category'], ['estimated_duration', 'Duration (e.g. 45 minutes)'], ['provider', 'Provider'],
];

// Create (course = null) or edit an external course
export default function ExternalCourseEditDialog({ open, course, token, onClose, onSaved, onThumbChange }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [thumb, setThumb] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingUrl, setPendingUrl] = useState('');

  useEffect(() => {
    if (open) {
      setForm(course ? { ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, course[k] ?? EMPTY[k]])) } : EMPTY);
      setThumb(course?.thumbnail_url || null);
      setPendingFile(null);
      setPendingUrl('');
    }
  }, [open, course]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.title.trim() || !form.iframe_url.trim().toLowerCase().startsWith('https://')) {
      toast.error('A title and an https:// course link are required');
      return;
    }
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const body = { ...form, cpd_points: Number(form.cpd_points) || 0 };
      if (course) await axios.patch(`${API_BASE}/api/admin/external-courses/${course.course_id}`, body, { headers });
      else {
        const res = await axios.post(`${API_BASE}/api/admin/external-courses`, body, { headers });
        const newId = res.data?.course_id || res.data?.course?.course_id;
        if (newId && (pendingFile || pendingUrl.trim())) {
          const base = `${API_BASE}/api/admin/external-courses/${newId}`;
          try {
            if (pendingFile) {
              const fd = new FormData();
              fd.append('file', pendingFile);
              await axios.post(`${base}/thumbnail`, fd, { headers });
            } else {
              await axios.post(`${base}/thumbnail-from-url`, { url: pendingUrl.trim() }, { headers });
            }
          } catch (e) {
            toast.warning(`Course added, but the thumbnail failed: ${thumbnailErrorMessage(e)}`);
          }
        }
      }
      toast.success(course ? 'External course updated' : 'External course added');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save the course');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-navigate-surface border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ext-edit-dialog">
        <DialogHeader><DialogTitle className="text-white">{course ? 'Edit external course' : 'Add external course'}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {FIELDS.map(([k, label]) => (
            <label key={k} className={`text-sm text-zinc-400 ${k === 'title' || k === 'iframe_url' ? 'sm:col-span-2' : ''}`}>
              {label}
              <Input value={form[k]} onChange={set(k)} className="mt-1 bg-zinc-900 border-zinc-700 text-white" data-testid={`ext-edit-${k}`} />
            </label>
          ))}
          <label className="text-sm text-zinc-400">Level
            <select value={form.level} onChange={set('level')} className="mt-1 w-full h-10 rounded-md bg-zinc-900 border border-zinc-700 text-white px-3" data-testid="ext-edit-level">
              {['Introductory', 'Intermediate', 'Advanced'].map((l) => <option key={l}>{l}</option>)}
            </select>
          </label>
          <label className="text-sm text-zinc-400">CPD points
            <Input type="number" min="0" step="0.5" value={form.cpd_points} onChange={set('cpd_points')} className="mt-1 bg-zinc-900 border-zinc-700 text-white" data-testid="ext-edit-cpd_points" />
          </label>
          <label className="text-sm text-zinc-400">Status
            <select value={form.status} onChange={set('status')} className="mt-1 w-full h-10 rounded-md bg-zinc-900 border border-zinc-700 text-white px-3" data-testid="ext-edit-status">
              <option value="published">Published</option>
              <option value="draft">Draft (hidden)</option>
            </select>
          </label>
          <label className="text-sm text-zinc-400 sm:col-span-2">Description
            <Textarea rows={3} value={form.description} onChange={set('description')} className="mt-1 bg-zinc-900 border-zinc-700 text-white" data-testid="ext-edit-description" />
          </label>
          {course ? (
            <ExternalCourseThumbnail course={{ ...course, thumbnail_url: thumb }} token={token} onChange={(url) => { setThumb(url); onThumbChange?.(); }} />
          ) : (
            <div className="sm:col-span-2" data-testid="ext-new-thumb">
              <p className="text-sm text-zinc-400 mb-2">Thumbnail (optional)</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input placeholder="Paste an image link (https://...)" value={pendingUrl} disabled={!!pendingFile}
                  onChange={(e) => setPendingUrl(e.target.value)} className="bg-zinc-900 border-zinc-700 text-white" data-testid="ext-new-thumb-url" />
                <span className="text-xs text-zinc-500 self-center">or</span>
                <label className="inline-flex items-center gap-2 px-3 h-9 rounded-md border border-zinc-700 text-sm text-zinc-200 cursor-pointer hover:border-navigate-gold shrink-0" data-testid="ext-new-thumb-upload">
                  <span className="max-w-[160px] truncate">{pendingFile ? pendingFile.name : 'Upload image'}</span>
                  <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="ext-new-thumb-file"
                    onChange={(e) => { setPendingFile(e.target.files?.[0] || null); setPendingUrl(''); }} />
                </label>
              </div>
              <p className="text-xs text-zinc-500 mt-1">You can also generate one with AI after the course is added.</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-zinc-700" data-testid="ext-edit-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="btn-gold" data-testid="ext-edit-save">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{course ? 'Save changes' : 'Add course'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
